import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, LoaderCircle, MessageSquare, X } from "lucide-react";

import { useConversationsApi } from "../api/conversationsApi";
import { formatText } from "../utils/messageHelpers";
import { getDayLabel } from "../utils/helpers";
import {
  contactLabel,
  formatPhone,
} from "../components/analysis/analysisLabels";
import AnalysisCoverage from "../components/analysis/AnalysisCoverage";

const mediaUrl = (path) => `${process.env.REACT_APP_URL}${path}`;

const MessageBubble = ({ message, highlight }) => {
  const isAgent = message.direction === "OUTGOING";
  const time = message.sentAt
    ? new Date(message.sentAt).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-lg px-3 py-2 ${
          isAgent
            ? "bg-green-500 text-white"
            : "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
        } ${highlight ? "ring-2 ring-amber-400" : ""}`}
      >
        {isAgent && message.sentBy?.name && (
          <p className="mb-0.5 text-xs font-semibold text-green-50">
            {message.sentBy.name}
          </p>
        )}

        {message.messageType === "image" && message.mediaPath && (
          <img
            src={mediaUrl(message.mediaPath)}
            loading="lazy"
            alt="imagem enviada"
            className="mb-1 max-w-[220px] rounded-lg"
          />
        )}

        {message.messageType === "audio" && message.mediaPath && (
          <audio controls preload="none" className="mb-1 w-48">
            <source src={mediaUrl(message.mediaPath)} />
          </audio>
        )}

        {message.messageType === "video" && message.mediaPath && (
          <video
            controls
            preload="none"
            className="mb-1 max-w-[220px] rounded-lg"
            src={mediaUrl(message.mediaPath)}
          />
        )}

        {message.messageType === "document" && message.mediaPath && (
          <a
            href={mediaUrl(message.mediaPath)}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-1 block rounded bg-black/10 px-2 py-1 text-sm underline"
          >
            {message.mediaPath.split("/").pop() || "Documento"}
          </a>
        )}

        {message.messageBody && (
          <p className="whitespace-pre-wrap break-words text-sm">
            {formatText(message.messageBody)}
          </p>
        )}

        <p
          className={`mt-1 text-right text-xs ${
            isAgent ? "text-green-100" : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {time}
        </p>
      </div>
    </div>
  );
};

const ConversationTranscriptModal = ({ item, onClose, onOpenFull }) => {
  const api = useConversationsApi();
  const [transcript, setTranscript] = useState(null);
  const [error, setError] = useState(null);
  const analysis = item.analysis;

  useEffect(() => {
    let cancelled = false;
    setTranscript(null);
    setError(null);

    api
      .getTranscript(item.conversationId)
      .then((data) => {
        if (!cancelled) setTranscript(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [api, item.conversationId]);

  useEffect(() => {
    const onKeyDown = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // A IA cita trechos como evidência; destacamos as mensagens correspondentes
  // para o gestor conferir a citação sem procurar na conversa inteira.
  const evidences = useMemo(
    () =>
      [
        ...(analysis.customerBehavior?.evidence || []),
        ...(analysis.agentBehavior?.evidence || []),
      ]
        .map((evidence) => evidence.trim().toLowerCase())
        .filter((evidence) => evidence.length >= 12),
    [analysis],
  );

  const isEvidence = useCallback(
    (body) => {
      if (!body) return false;
      const text = body.toLowerCase();
      return evidences.some(
        (evidence) => text.includes(evidence) || evidence.includes(text),
      );
    },
    [evidences],
  );

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const message of transcript?.messages || []) {
      if (!message.sentAt) continue;
      const day = getDayLabel(message.sentAt);
      groups.set(day, [...(groups.get(day) || []), message]);
    }
    return [...groups.entries()];
  }, [transcript]);

  const name = contactLabel(item.contact);
  const phone = formatPhone(item.contact?.phoneNumber);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(event) => event.target === event.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900"
        >
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                {name}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {phone && phone !== name && `${phone} · `}
                {item.messageCount} mensagens · prioridade {item.priority.score}
                {transcript?.truncated &&
                  ` · exibindo as ${transcript.messages.length} mais recentes`}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {onOpenFull && (
                <button
                  onClick={() => onOpenFull(item.conversationId)}
                  className="flex items-center gap-1 rounded-lg bg-green-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-600"
                >
                  <ExternalLink className="h-4 w-4" />
                  Abrir no chat
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1">
            <div className="flex-1 overflow-y-auto bg-gray-100 p-4 dark:bg-gray-950">
              {error && (
                <p className="text-center text-sm text-red-500">
                  Erro ao carregar a conversa: {error}
                </p>
              )}

              {!transcript && !error && (
                <div className="flex h-full items-center justify-center text-gray-500 dark:text-gray-400">
                  <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                  Carregando conversa...
                </div>
              )}

              {grouped.map(([day, messages]) => (
                <div key={day} className="mb-4 space-y-3">
                  <div className="flex justify-center">
                    <span className="rounded-full bg-gray-300 px-3 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      {day}
                    </span>
                  </div>
                  {messages.map((message) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      highlight={isEvidence(message.messageBody)}
                    />
                  ))}
                </div>
              ))}

              {transcript && grouped.length === 0 && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  Esta conversa não tem mensagens registradas.
                </p>
              )}
            </div>

            <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-gray-200 p-4 dark:border-gray-700 lg:block">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Resumo da IA
                </p>
                <AnalysisCoverage analysis={analysis} />
              </div>
              <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
                {analysis.summary}
              </p>

              {analysis.dissatisfactionSignals?.length > 0 && (
                <div className="mb-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                    Sinais de insatisfação
                  </p>
                  <ul className="space-y-1">
                    {analysis.dissatisfactionSignals.map((signal, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 dark:text-gray-300"
                      >
                        • {signal}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.recommendedActions?.length > 0 && (
                <div className="mb-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400">
                    Próximas ações
                  </p>
                  <ul className="space-y-1">
                    {analysis.recommendedActions.map((action, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 dark:text-gray-300"
                      >
                        • {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {evidences.length > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  As mensagens citadas pela IA como evidência estão destacadas na
                  conversa.
                </p>
              )}
            </aside>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConversationTranscriptModal;
